import { useCallback, useEffect, useRef, useState } from "react";
import * as O from "@/components/Ornaments";
import { useSound } from "@/lib/sound";
import { useT } from "@/lib/i18n";
import {
  GuestPhoto,
  CountdownSection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import type { VariantProps } from "./types";

// ═══════════════════════════════════════════════════════════════════════
// 4. CEREMONIAL — Nikoh Royal
//
// Reverent Islamic ceremony page. The signature is the iwan portal hero:
// a Samarkand/Bukhara-style four-centred pointed arch that draws itself
// on first paint while the photo fades in beneath. Sections are
// separated by an 8-fold star tessellation strip, and the map
// section sits over a low-opacity Uzbek dome silhouette.
//
// Cultural notes:
//   • The Quran verse (Ar-Rum 21) is rendered in Arabic and Uzbek side
//     by side, with the surah label set apart. No music, no guestbook
//     — this is family-formal religious content.
//   • Reveals are slow (1.2s ease-out) and respect
//     `prefers-reduced-motion: reduce` strictly.
//   • Sound is restrained: a single dignified `chime` on RSVP
//     confirmation, and a soft `tap` on the two primary CTAs.
// ═══════════════════════════════════════════════════════════════════════

// ─── Pointed-arch geometry (shared with arch-photo layout) ────────────
// 200×270 viewBox, four-centred Persian arch.
const ARCH_PATH =
  "M 10 268 L 10 168 C 10 90 60 18 100 12 C 140 18 190 90 190 168 L 190 268 Z";

// ─── prefers-reduced-motion hook (no extra deps) ──────────────────────
function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e: MediaQueryListEvent) => setReduce(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return reduce;
}

// ─── Reveal-on-scroll wrapper (1.2s, dignified) ───────────────────────
interface RevealProps {
  children: React.ReactNode;
  delay?: number;
  reduce: boolean;
}

function Reveal({ children, delay = 0, reduce }: RevealProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(reduce);

  useEffect(() => {
    if (reduce) {
      setShown(true);
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduce]);

  return (
    <div
      ref={ref}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(18px)",
        transition: reduce ? "none" : `opacity 1.2s ease-out ${delay}ms, transform 1.2s ease-out ${delay}ms`,
        willChange: reduce ? undefined : "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

// ─── Reusable IDs for SVG defs (keep stable across renders) ───────────
const TILE_PATTERN_ID = "nikoh-tile-band";
const DOME_PATTERN_ID = "nikoh-dome-bg";

// ─── Tile-strip divider — 8-fold star tessellation across full width ──
interface TileStripProps {
  accent: string;
  ink: string;
}

function TileStrip({ accent, ink }: TileStripProps) {
  // Mobile rule (in <style> below): wraps to 80% viewport width while
  // keeping tessellation density unchanged (cell stays 32×28 user units).
  return (
    <div className="nikoh-tile-strip" aria-hidden style={{ width: "100%", height: 28, overflow: "hidden", opacity: 0.65, margin: "0 auto" }}>
      <svg width="100%" height="28" preserveAspectRatio="xMidYMid slice">
        <defs>
          <pattern id={`${TILE_PATTERN_ID}-strip`} x="0" y="0" width="32" height="28" patternUnits="userSpaceOnUse">
            <g stroke={accent} strokeWidth="0.7" fill="none">
              {/* Octagon */}
              <path d="M16 2 L26 6 L30 14 L26 22 L16 26 L6 22 L2 14 L6 6 Z" />
              {/* Inner 8-point star */}
              <path
                d="M16 6 L19 12 L25 14 L19 16 L16 22 L13 16 L7 14 L13 12 Z"
                fill={accent}
                fillOpacity="0.12"
              />
              <circle cx="16" cy="14" r="1" fill={ink} opacity="0.4" />
            </g>
          </pattern>
        </defs>
        <rect width="100%" height="28" fill={`url(#${TILE_PATTERN_ID}-strip)`} />
      </svg>
    </div>
  );
}

// ─── Mahalla courtyard / dome silhouette backdrop for map section ─────
interface DomeBackdropProps {
  accent: string;
}

function DomeBackdrop({ accent }: DomeBackdropProps) {
  return (
    <svg
      aria-hidden
      className="nikoh-dome-backdrop"
      viewBox="0 0 800 200"
      preserveAspectRatio="xMidYMax slice"
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        height: "60%",
        pointerEvents: "none",
        opacity: 0.08,
      }}
    >
      <defs>
        <pattern id={DOME_PATTERN_ID} x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
          <circle cx="3" cy="3" r="0.6" fill={accent} />
        </pattern>
      </defs>
      {/* Skyline of mahalla courtyard: low walls, gates, two domes,
          a slender minaret. Flat illustration, all in accent. */}
      <g fill={accent}>
        {/* Long courtyard wall */}
        <rect x="0" y="150" width="800" height="50" />
        {/* Crenellation along the top of the wall */}
        {Array.from({ length: 40 }).map((_, i) => (
          <rect key={i} x={i * 20} y="146" width="10" height="6" />
        ))}
        {/* Gate (mahalla guzari arch) */}
        <path d="M 360 150 L 360 110 C 360 90 380 80 400 80 C 420 80 440 90 440 110 L 440 150 Z" />
        {/* Left dome */}
        <ellipse cx="180" cy="150" rx="55" ry="14" />
        <path d="M 130 150 C 130 110 230 110 230 150 Z" />
        <rect x="178" y="78" width="4" height="32" />
        <circle cx="180" cy="74" r="6" />
        {/* Right dome */}
        <ellipse cx="620" cy="150" rx="65" ry="16" />
        <path d="M 560 150 C 560 100 680 100 680 150 Z" fill={accent} />
        <rect x="618" y="68" width="4" height="32" />
        <circle cx="620" cy="64" r="7" />
        {/* Minaret far right */}
        <rect x="740" y="40" width="6" height="110" />
        <circle cx="743" cy="36" r="6" fill="none" stroke={accent} strokeWidth="2" />
      </g>
      <rect width="800" height="200" fill={`url(#${DOME_PATTERN_ID})`} opacity="0.4" />
    </svg>
  );
}

// ─── Hero iwan portal with self-drawing arch + photo fade ─────────────
interface ArchHeroProps {
  src: string | undefined;
  accent: string;
  ink: string;
  bg: string;
  reduce: boolean;
}

function ArchHero({ src, accent, ink, bg, reduce }: ArchHeroProps) {
  // Trigger animation after first paint.
  const [drawn, setDrawn] = useState(reduce);
  useEffect(() => {
    if (reduce) {
      setDrawn(true);
      return;
    }
    // Defer to the next frame so the initial style applies before transitioning.
    const id = window.requestAnimationFrame(() => setDrawn(true));
    return () => window.cancelAnimationFrame(id);
  }, [reduce]);

  // Approximate length of ARCH_PATH at this scale; pad upwards so the
  // dasharray fully clears once animated.
  const STROKE_LEN = 900;

  // Photo fades in slightly after the arch finishes drawing (feels like
  // the portal opening to reveal the couple).
  const photoDelay = reduce ? 0 : 1400;

  // Mobile gets a denser variant of the same 8-fold tessellation so the
  // ribbon still reads at smaller pixel sizes. Pattern cell: 9×9 vs 14×14.
  const MOBILE_TILE_PATTERN_ID = `${TILE_PATTERN_ID}-mobile`;

  return (
    <div
      className="nikoh-arch-hero"
      style={{
        width: "min(440px, 84vw)",
        aspectRatio: "200/270",
        position: "relative",
        marginBottom: 48,
      }}
    >
      {/* Photo, clipped to the arch silhouette via SVG <clipPath>. */}
      <svg
        viewBox="0 0 200 270"
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      >
        <defs>
          <clipPath id="nikoh-arch-clip" clipPathUnits="userSpaceOnUse">
            <path d={ARCH_PATH} />
          </clipPath>

          {/* 8-fold tessellation, used along the arch ribbon. */}
          <pattern id={TILE_PATTERN_ID} x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
            <g stroke={accent} strokeWidth="0.55" fill="none">
              <path d="M7 0.5 L11.6 2.4 L13.5 7 L11.6 11.6 L7 13.5 L2.4 11.6 L0.5 7 L2.4 2.4 Z" />
              <path
                d="M7 2.5 L8.4 5.6 L11.5 7 L8.4 8.4 L7 11.5 L5.6 8.4 L2.5 7 L5.6 5.6 Z"
                fill={accent}
                fillOpacity="0.18"
              />
              <path d="M7 0.5 L7 13.5 M0.5 7 L13.5 7" strokeWidth="0.25" opacity="0.5" />
            </g>
          </pattern>

          {/* Denser mobile variant — same 8-fold star, smaller cell. */}
          <pattern id={MOBILE_TILE_PATTERN_ID} x="0" y="0" width="9" height="9" patternUnits="userSpaceOnUse">
            <g stroke={accent} strokeWidth="0.45" fill="none">
              <path d="M4.5 0.3 L7.5 1.6 L8.7 4.5 L7.5 7.4 L4.5 8.7 L1.5 7.4 L0.3 4.5 L1.5 1.6 Z" />
              <path
                d="M4.5 1.6 L5.4 3.6 L7.4 4.5 L5.4 5.4 L4.5 7.4 L3.6 5.4 L1.6 4.5 L3.6 3.6 Z"
                fill={accent}
                fillOpacity="0.18"
              />
            </g>
          </pattern>

          {/* Mask carving a tile-band along the arch outline. */}
          <mask id="nikoh-arch-band">
            <rect width="200" height="270" fill="black" />
            <path d={ARCH_PATH} stroke="white" strokeWidth="9" fill="none" />
            <path d={ARCH_PATH} stroke="black" strokeWidth="1.5" fill="none" />
          </mask>
        </defs>

        {/* Cream wash inside the arch — fills before the photo fades in. */}
        <g clipPath="url(#nikoh-arch-clip)">
          <rect width="200" height="270" fill={bg} />
        </g>

        {/* Tile band (desktop tessellation) — fades in on first paint.
            Hidden on mobile via .nikoh-arch-tiles-desktop rule. */}
        <rect
          className="nikoh-arch-tiles-desktop"
          width="200"
          height="270"
          fill={`url(#${TILE_PATTERN_ID})`}
          mask="url(#nikoh-arch-band)"
          style={{
            opacity: drawn ? 1 : 0,
            transition: reduce ? "none" : "opacity 0.6s ease-out 1.2s",
          }}
        />

        {/* Tile band (mobile-only, denser) — only painted under 768px. */}
        <rect
          className="nikoh-arch-tiles-mobile"
          width="200"
          height="270"
          fill={`url(#${MOBILE_TILE_PATTERN_ID})`}
          mask="url(#nikoh-arch-band)"
          style={{
            opacity: drawn ? 1 : 0,
            transition: reduce ? "none" : "opacity 0.6s ease-out 1.2s",
            display: "none",
          }}
        />

        {/* Outer arch outline — draws itself with a stroke-dasharray sweep. */}
        <path
          d={ARCH_PATH}
          fill="none"
          stroke={accent}
          strokeWidth="1.5"
          strokeDasharray={STROKE_LEN}
          strokeDashoffset={drawn ? 0 : STROKE_LEN}
          style={{
            transition: reduce ? "none" : "stroke-dashoffset 1.6s cubic-bezier(0.22, 0.61, 0.36, 1) 0.1s",
          }}
        />

        {/* Inner hairline arch */}
        <path
          d="M 16 268 L 16 170 C 16 96 64 28 100 22 C 136 28 184 96 184 170 L 184 268"
          fill="none"
          stroke={accent}
          strokeWidth="0.6"
          opacity={drawn ? 0.7 : 0}
          style={{
            transition: reduce ? "none" : "opacity 0.8s ease-out 1.0s",
          }}
        />

        {/* Apex medallion — a small Suzani-style finial */}
        <g
          transform="translate(100 6)"
          style={{
            opacity: drawn ? 1 : 0,
            transform: drawn ? "translate(100px, 6px) scale(1)" : "translate(100px, 6px) scale(0.4)",
            transformOrigin: "100px 6px",
            transition: reduce ? "none" : "opacity 0.6s ease-out 1.6s, transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 1.4s",
          }}
        >
          <circle r="3" fill={accent} />
          <path
            d="M 0 -6 L 1.7 -1.7 L 6 0 L 1.7 1.7 L 0 6 L -1.7 1.7 L -6 0 L -1.7 -1.7 Z"
            fill={accent}
            opacity="0.4"
          />
        </g>
      </svg>

      {/* Spandrel rosettes (top corners outside the arch). */}
      <svg
        viewBox="0 0 200 270"
        preserveAspectRatio="none"
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          opacity: drawn ? 1 : 0,
          transition: reduce ? "none" : "opacity 0.8s ease-out 1.4s",
        }}
      >
        {[
          { x: 6, y: 14 },
          { x: 172, y: 14 },
        ].map((p, i) => (
          <g key={i} transform={`translate(${p.x} ${p.y})`} stroke={accent} strokeWidth="0.5" fill="none">
            <circle cx="11" cy="11" r="10" />
            <circle cx="11" cy="11" r="5" />
            {Array.from({ length: 8 }).map((_, j) => {
              const a = (j * 45 * Math.PI) / 180;
              return (
                <line
                  key={j}
                  x1={11 + Math.cos(a) * 5}
                  y1={11 + Math.sin(a) * 5}
                  x2={11 + Math.cos(a) * 10}
                  y2={11 + Math.sin(a) * 10}
                />
              );
            })}
            <circle cx="11" cy="11" r="1.4" fill={accent} stroke="none" />
          </g>
        ))}
      </svg>

      {/* The photo — fades in inside the arch silhouette after the arch
          has finished drawing. Using clip-path so it tracks the same
          geometry as the SVG outline. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          // CSS path clip mirroring ARCH_PATH (using % so it scales).
          clipPath:
            "path('M 5% 99.3% L 5% 62.2% C 5% 33.3% 30% 6.7% 50% 4.4% C 70% 6.7% 95% 33.3% 95% 62.2% L 95% 99.3% Z')",
          overflow: "hidden",
          opacity: drawn ? 1 : 0,
          transform: drawn ? "scale(1)" : "scale(0.98)",
          transformOrigin: "50% 70%",
          transition: reduce
            ? "none"
            : `opacity 1.0s ease-out ${photoDelay}ms, transform 1.4s ease-out ${photoDelay}ms`,
        }}
      >
        <GuestPhoto src={src} style={{ position: "absolute", inset: 0 }} fallbackBg={bg} fallbackTone={accent} />
        {/* Subtle vignette evoking aged tile glaze */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(180deg, transparent 60%, ${ink}33 100%)`,
            pointerEvents: "none",
          }}
        />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// VARIANT
// ═══════════════════════════════════════════════════════════════════════
export function VariantCeremonial({ t, isDark, ctx }: VariantProps) {
  const p = t.photos || [];
  const reduce = usePrefersReducedMotion();
  const { play } = useSound();
  const tt = useT();

  // Wrap rsvp setter so we can play a dignified `chime` on a "yes"
  // confirmation and a softer `tap` on "no". This is the only place we
  // touch sound on form submit.
  const baseSetRsvp = ctx.rsvp.setRsvp;
  const setRsvpWithSound = useCallback(
    (v: string | null) => {
      if (v === "yes") play("chime");
      else if (v === "no") play("tap");
      baseSetRsvp(v);
    },
    [baseSetRsvp, play],
  );

  // Sound for primary CTAs (countdown/share/etc are not wired — only
  // direct buttons we own). Keep it to the absolute minimum.
  // On mobile we drop this entirely — only the RSVP `chime` survives, so
  // the page reads as 1-sound dignity instead of 3-sound chatter.
  const onPrimary = useCallback(() => {
    if (
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(max-width: 768px)").matches
    ) {
      return; // mobile sound budget = 1 (chime on RSVP only)
    }
    play("tap");
  }, [play]);

  return (
    <div className="nikoh-variant">
      {/* ─── HERO: iwan portal ───────────────────────────────────────── */}
      <section
        className="nikoh-hero"
        style={{
          minHeight: "100vh",
          padding: "60px 24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          position: "relative",
        }}
      >
        {/* Bismillah inscription band — like the calligraphy over an iwan */}
        <div
          style={{
            position: "absolute",
            top: 60,
            left: "50%",
            transform: "translateX(-50%)",
            color: t.accent,
          }}
        >
          <O.Flourish width={180} color={t.accent} />
        </div>

        <div
          className="nikoh-bismillah-eyebrow"
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: t.accent,
            marginBottom: 8,
            marginTop: 24,
          }}
        >
          {tt("variant.n01.hero.eyebrow")}
        </div>
        {/* Arabic Basmala — sets the tone before the names. On mobile this
            block is reordered ABOVE the Uzbek transliteration via flex
            `order` (more meaningful for religious context). */}
        <div
          className="nikoh-bismillah-arabic"
          dir="rtl"
          lang="ar"
          aria-hidden="true"
          style={{
            fontFamily: "'Amiri', 'Scheherazade New', 'Noto Naskh Arabic', serif",
            fontSize: "clamp(22px, 3vw, 32px)",
            color: t.ink,
            marginBottom: 32,
            fontFeatureSettings: '"kern", "liga"',
            opacity: 0.85,
          }}
        >
          بِسْمِ ٱللَّٰهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
        </div>
        <span className="sr-only">Bismillah ir-Rahman ir-Rahim</span>

        <ArchHero src={p[0]} accent={t.accent} ink={t.ink} bg={t.bg} reduce={reduce} />

        <h1
          className="nikoh-couple-names"
          style={{
            fontFamily: t.font,
            fontSize: "clamp(48px, 7vw, 88px)",
            fontWeight: 400,
            letterSpacing: "0.04em",
            marginBottom: 16,
            opacity: 0,
            animation: reduce ? "none" : "nikohFadeIn 1.2s ease-out 1.6s forwards",
          }}
        >
          {t.names.split("&")[0]}
          <span className="nikoh-couple-amp" style={{ color: t.accent, fontStyle: "italic", margin: "0 .3em" }}>&</span>
          {t.names.split("&")[1]}
        </h1>
        <div
          style={{
            fontFamily: t.font,
            fontStyle: "italic",
            fontSize: 24,
            color: t.accent,
            marginTop: 16,
            opacity: 0,
            animation: reduce ? "none" : "nikohFadeIn 1.2s ease-out 1.9s forwards",
          }}
        >
          {t.date} · {t.venue}
        </div>
        <div
          style={{
            marginTop: 32,
            color: t.accent,
            opacity: 0,
            animation: reduce ? "none" : "nikohFadeIn 1.0s ease-out 2.2s forwards",
          }}
        >
          <O.Diamond size={24} color={t.accent} />
        </div>

        {/* Reduced-motion fallback / ambient: pre-show all the
            opacity-animated names, dates, etc. via an inline rule. */}
        <style>{`
          @keyframes nikohFadeIn {
            from { opacity: 0; transform: translateY(8px); }
            to   { opacity: 1; transform: translateY(0);   }
          }
          @media (prefers-reduced-motion: reduce) {
            .nikoh-prefers-static, .nikoh-prefers-static * {
              animation: none !important;
              transition: none !important;
            }
          }
          .sr-only { position:absolute !important; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
        `}</style>
      </section>

      <TileStrip accent={t.accent} ink={t.ink} />

      {/* ─── Two-up details strip — venue + ceremony photos ─────────── */}
      {(p[1] || p[2]) && (
        <Reveal reduce={reduce}>
          <section
            style={{
              padding: 0,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 0,
              borderTop: `1px solid ${t.accent}44`,
              borderBottom: `1px solid ${t.accent}44`,
            }}
          >
            {[p[1], p[2]].filter(Boolean).map((src, i) => (
              <div
                key={i}
                style={{
                  position: "relative",
                  aspectRatio: "4/3",
                  borderRight: i === 0 ? `1px solid ${t.accent}44` : "none",
                }}
              >
                <GuestPhoto src={src} style={{ position: "absolute", inset: 0 }} fallbackBg={t.bg} fallbackTone={t.accent} />
                <div
                  style={{
                    position: "absolute",
                    left: 24,
                    bottom: 24,
                    padding: "10px 18px",
                    background: t.bg,
                    fontFamily: t.font,
                    fontStyle: "italic",
                    fontSize: 15,
                    color: t.ink,
                    border: `1px solid ${t.accent}66`,
                  }}
                >
                  {i === 0 ? tt("variant.n01.detail.0.label") : tt("variant.n01.detail.1.label")}
                </div>
              </div>
            ))}
          </section>
        </Reveal>
      )}

      <TileStrip accent={t.accent} ink={t.ink} />

      {/* ─── QUR'ON KARIM — Ar-Rum 21 (Arabic + Uzbek) ──────────────── */}
      <Reveal reduce={reduce}>
        <section
          className="nikoh-mihrab"
          style={{
            padding: "120px 32px",
            background: `${t.accent}10`,
            textAlign: "center",
            position: "relative",
          }}
        >
          {/* Subtle corner ornaments framing the verse like a mihrab niche.
              On mobile these scale down to ~32px and stay tight to corners. */}
          <div className="nikoh-mihrab-rosette" style={{ position: "absolute", top: 24, left: 24, color: t.accent, opacity: 0.5 }}>
            <O.Suzani size={48} color={t.accent} />
          </div>
          <div className="nikoh-mihrab-rosette" style={{ position: "absolute", top: 24, right: 24, color: t.accent, opacity: 0.5 }}>
            <O.Suzani size={48} color={t.accent} />
          </div>
          <div className="nikoh-mihrab-rosette" style={{ position: "absolute", bottom: 24, left: 24, color: t.accent, opacity: 0.5 }}>
            <O.Suzani size={48} color={t.accent} />
          </div>
          <div className="nikoh-mihrab-rosette" style={{ position: "absolute", bottom: 24, right: 24, color: t.accent, opacity: 0.5 }}>
            <O.Suzani size={48} color={t.accent} />
          </div>

          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.5em",
              color: t.accent,
              marginBottom: 12,
            }}
          >
            {tt("variant.n01.quran.eyebrow")}
          </div>
          <div
            style={{
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: 18,
              color: t.ink,
              opacity: 0.7,
              marginBottom: 56,
            }}
          >
            {tt("variant.n01.quran.surah")}
          </div>

          {/* Arabic — the original (public domain) */}
          <p
            className="nikoh-mihrab-arabic"
            dir="rtl"
            lang="ar"
            aria-hidden="true"
            style={{
              fontFamily: "'Amiri', 'Scheherazade New', 'Noto Naskh Arabic', serif",
              fontSize: "clamp(26px, 3.6vw, 42px)",
              lineHeight: 2.0,
              color: t.ink,
              maxWidth: 880,
              margin: "0 auto 48px",
              fontFeatureSettings: '"kern", "liga", "calt"',
            }}
          >
            وَمِنْ آيَاتِهِ أَنْ خَلَقَ لَكُم مِّنْ أَنفُسِكُمْ أَزْوَاجًا لِّتَسْكُنُوا إِلَيْهَا وَجَعَلَ بَيْنَكُم مَّوَدَّةً وَرَحْمَةً ۚ إِنَّ فِي ذَٰلِكَ لَآيَاتٍ لِّقَوْمٍ يَتَفَكَّرُونَ
          </p>
          <span className="sr-only">Wa min ayatihi an khalaqa lakum min anfusikum azwajan li-taskunu ilayha</span>

          {/* A delicate divider between Arabic and Uzbek */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 40, color: t.accent }}>
            <O.Flourish width={140} color={t.accent} />
          </div>

          {/* Uzbek translation — preserved verbatim */}
          <p
            className="nikoh-mihrab-uzbek"
            style={{
              fontFamily: t.font,
              fontSize: "clamp(22px, 2.8vw, 32px)",
              lineHeight: 1.8,
              fontStyle: "italic",
              maxWidth: 800,
              margin: "0 auto",
              color: t.ink,
              fontFeatureSettings: '"kern"',
            }}
          >
            {tt("variant.n01.quran.uzbek")}
          </p>
        </section>
      </Reveal>

      <TileStrip accent={t.accent} ink={t.ink} />

      <Reveal reduce={reduce}>
        <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />
      </Reveal>

      <TileStrip accent={t.accent} ink={t.ink} />

      <Reveal reduce={reduce}>
        <div className="nikoh-schedule-wrap">
          <ScheduleSection
            t={t}
            items={[
              [tt("variant.n01.schedule.0.time"), tt("variant.n01.schedule.0.event")],
              [tt("variant.n01.schedule.1.time"), tt("variant.n01.schedule.1.event")],
              [tt("variant.n01.schedule.2.time"), tt("variant.n01.schedule.2.event")],
              [tt("variant.n01.schedule.3.time"), tt("variant.n01.schedule.3.event")],
            ]}
          />
        </div>
      </Reveal>

      <TileStrip accent={t.accent} ink={t.ink} />

      {/* Map section — wrapped with Uzbek dome silhouette backdrop. */}
      <Reveal reduce={reduce}>
        <div
          className="nikoh-map-wrap"
          style={{ position: "relative", overflow: "hidden" }}
          onClick={(e) => {
            // Soft `tap` on the primary "open in maps" CTA — captured at
            // wrapper level so we don't have to fork the section.
            const target = e.target as HTMLElement | null;
            if (target && target.closest("button")) onPrimary();
          }}
        >
          <DomeBackdrop accent={t.accent} />
          <div style={{ position: "relative" }}>
            <MapSection t={t} isDark={isDark} addressLine={`${t.venue} · ${tt("variant.n01.map.address")}`} />
          </div>
        </div>
      </Reveal>

      <TileStrip accent={t.accent} ink={t.ink} />

      {/* RSVP — the only place we play `chime`. Setter is wrapped above. */}
      <Reveal reduce={reduce}>
        <RsvpSection
          t={t}
          isDark={isDark}
          rsvp={ctx.rsvp.rsvp}
          setRsvp={setRsvpWithSound}
          guests={ctx.rsvp.guests}
          setGuests={ctx.rsvp.setGuests}
          guestName={ctx.rsvp.guestName}
          setGuestName={ctx.rsvp.setGuestName}
          label={tt("variant.n01.rsvp.label")}
        />
      </Reveal>

      <TileStrip accent={t.accent} ink={t.ink} />

      <Reveal reduce={reduce}>
        <ShareSection t={t} />
      </Reveal>

      {/* Mobile-only reverence stamp — small Arabic du'a above the footer.
          "Rabbi zidnii 'ilman" — "My Lord, increase me in knowledge"
          (Qur'an 20:114). Authentic religious closing without ornament.
          Hidden on desktop via `nikoh-mobile-only` rule below. */}
      <div className="nikoh-mobile-only nikoh-blessing-stamp" aria-hidden>
        <div
          dir="rtl"
          lang="ar"
          style={{
            fontFamily: "'Amiri', 'Scheherazade New', 'Noto Naskh Arabic', serif",
            fontSize: 18,
            color: t.accent,
            opacity: 0.55,
            letterSpacing: "0.02em",
            marginBottom: 4,
          }}
        >
          رَبِّ زِدْنِي عِلْمًا
        </div>
        <div
          style={{
            fontFamily: t.font,
            fontStyle: "italic",
            fontSize: 11,
            color: t.accent,
            opacity: 0.55,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}
        >
          {tt("variant.n01.blessing.translit")}
        </div>
      </div>

      <GuestFooter t={t} />

      {/* ──────────────────────────────────────────────────────────────
          Mobile rules — additive only. Desktop styles are not removed;
          these blocks are scoped to .nikoh-variant under 768px so they
          never bleed into desktop or other variants.
          ────────────────────────────────────────────────────────────── */}
      <style>{`
        /* Mobile-only stamp is hidden on desktop. */
        .nikoh-variant .nikoh-mobile-only { display: none; }

        @media (max-width: 768px) {
          /* 1. Iwan portal — vertical reframing (3:5) and density swap. */
          .nikoh-variant .nikoh-arch-hero {
            width: min(360px, 88vw) !important;
            aspect-ratio: 3 / 5 !important;
            margin-bottom: 36px !important;
          }
          .nikoh-variant .nikoh-arch-tiles-desktop { display: none !important; }
          .nikoh-variant .nikoh-arch-tiles-mobile  { display: inline !important; }

          /* 2. Bismillah — Arabic above transliteration on mobile.
                Uses flex order so the DOM stays untouched. */
          .nikoh-variant .nikoh-hero { padding: 64px 18px 48px !important; text-align: center; }
          .nikoh-variant .nikoh-bismillah-eyebrow {
            order: 2;
            font-size: 10px !important;
            letter-spacing: 0.42em !important;
            margin-top: 14px !important;
            margin-bottom: 6px !important;
          }
          .nikoh-variant .nikoh-bismillah-arabic {
            order: 1;
            font-size: 26px !important;
            margin-top: 32px !important;
            margin-bottom: 8px !important;
            opacity: 0.9 !important;
          }
          /* Pin the SVG flourish so its absolute positioning doesn't
             collide with the reordered eyebrow. */
          .nikoh-variant .nikoh-hero > svg:first-child,
          .nikoh-variant .nikoh-hero > div:first-child { top: 32px !important; }

          /* 3. Couple names — Marcellus 56–72px range, wine-red ampersand. */
          .nikoh-variant .nikoh-couple-names {
            font-size: clamp(36px, 12vw, 64px) !important;
            letter-spacing: 0.04em !important;
            line-height: 1.05 !important;
            margin-bottom: 12px !important;
          }
          .nikoh-variant button { min-height: 44px; }

          /* 4. Mihrab — fills near a full screen height on mobile. */
          .nikoh-variant .nikoh-mihrab {
            min-height: 92vh;
            padding: 80px 20px !important;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }
          .nikoh-variant .nikoh-mihrab-rosette { transform: scale(0.6667); transform-origin: top left; opacity: 0.45 !important; }
          .nikoh-variant .nikoh-mihrab-rosette:nth-of-type(2) { transform-origin: top right; }
          .nikoh-variant .nikoh-mihrab-rosette:nth-of-type(3) { transform-origin: bottom left; }
          .nikoh-variant .nikoh-mihrab-rosette:nth-of-type(4) { transform-origin: bottom right; }
          .nikoh-variant .nikoh-mihrab-arabic {
            font-size: 24px !important;
            line-height: 2.0 !important;
            margin-bottom: 32px !important;
            padding: 0 4px;
          }
          .nikoh-variant .nikoh-mihrab-uzbek {
            font-size: 17px !important;
            line-height: 1.7 !important;
            font-style: italic;
            padding: 0 4px;
          }

          /* 5. Tile-pattern divider — 80% width, density preserved. */
          .nikoh-variant .nikoh-tile-strip {
            width: 80% !important;
          }

          /* 6. Mahalla skyline backdrop — anchored, semi-cropped, fainter. */
          .nikoh-variant .nikoh-dome-backdrop {
            width: 90% !important;
            left: 5% !important;
            right: 5% !important;
            opacity: 0.06 !important;
            height: 45% !important;
          }

          /* 7. Schedule — bold serif times in 22–24px Marcellus, event
                name below. We target the inner spans of the shared
                ScheduleSection from our wrapper for mobile-only override. */
          .nikoh-variant .nikoh-schedule-wrap section {
            padding: 64px 24px !important;
          }
          .nikoh-variant .nikoh-schedule-wrap section h2 {
            font-size: 32px !important;
          }
          .nikoh-variant .nikoh-schedule-wrap section > div > div > div {
            grid-template-columns: 96px 1fr !important;
            gap: 18px !important;
            padding: 18px 0 !important;
          }
          .nikoh-variant .nikoh-schedule-wrap section > div > div > div > span:first-child {
            font-size: 23px !important;
            font-style: normal !important;
            letter-spacing: 0.02em;
          }
          .nikoh-variant .nikoh-schedule-wrap section > div > div > div > span:last-child {
            font-size: 16px !important;
            font-family: ${JSON.stringify(t.font)}, serif !important;
            letter-spacing: 0.01em;
          }

          /* 8. Map section — soften any pin-style affordances, lean serif. */
          .nikoh-variant .nikoh-map-wrap section { padding: 64px 20px !important; }
          .nikoh-variant .nikoh-map-wrap section h2 { font-size: 30px !important; }

          /* 10. Reverence stamp — visible only on mobile. */
          .nikoh-variant .nikoh-mobile-only.nikoh-blessing-stamp {
            display: block;
            text-align: center;
            padding: 36px 24px 12px;
          }
        }
      `}</style>
    </div>
  );
}
